import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useCurrencyHelpers } from '@plato/app-shared/hooks/use-currency-helpers';
import { InsInsightOutcomeResponse } from '@plato/data/prisma';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import {
    Card,
    CardContent,
    CardDetail,
    CardExpandAction,
    CardExpandContent,
    CardFront,
    CardHeader,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import type { TFunction } from 'i18next';
import { SparklesIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import { getInsightOutcomeEntityName } from '../../insights-common/lib/insight-entity-name.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';

function getOutcomeLogTitle(
    t: TFunction<'activity-feed'>,
    response: InsInsightOutcomeResponse,
    options: { insightType: string; entity: string },
): string {
    switch (response) {
        case InsInsightOutcomeResponse.INTERESTED:
            return t('log__insight_interested', options);
        case InsInsightOutcomeResponse.NO_NEED:
            return t('log__insight_no_need', options);
        case InsInsightOutcomeResponse.BUYS_ELSEWHERE:
            return t('log__insight_buys_elsewhere', options);
        // INCORRECT and NORMAL are the responses of a NOT_RELEVANT outcome.
        case InsInsightOutcomeResponse.INCORRECT:
        case InsInsightOutcomeResponse.NORMAL:
            return t('log__insight_not_relevant', options);
    }
}

export type ActivityFeedInsightOutcomeItemProps = {
    payload: InsInsightOutcomeDTO;
};

/**
 * One triaged insight as a grey log line: what was marked how, and who did it. When the
 * outcome has a note or the insight a value, the line folds open to show them.
 *
 * Takes the outcome rather than a feed record, so a visit's logged outcomes —
 * which never see a feed record — render the same card.
 */
export function ActivityFeedInsightOutcomeItem({
    payload,
}: ActivityFeedInsightOutcomeItemProps): ReactElement {
    const { t } = useTranslation('activity-feed');
    const { toCurrencyString } = useCurrencyHelpers();
    const { enableNeverBoughtCrossSellCopy } = useAppConfig();

    const title = getOutcomeLogTitle(t, payload.response, {
        insightType: getInsightName(t, payload.insight, {
            neverBoughtCrossSell: enableNeverBoughtCrossSellCopy,
        }),
        entity: getInsightOutcomeEntityName(t, payload),
    });
    const value = payload.insight.absolute_number
        ? toCurrencyString(Math.abs(roundAbsToHundred(payload.insight.absolute_number)))
        : undefined;
    const canFold = !!payload.text || value !== undefined;

    return (
        <Card>
            <CardFront>
                <CardStart render={<SparklesIcon />} />
                <CardContent className="min-w-0">
                    <CardHeader>
                        <CardTitle className="line-clamp-2 text-content-weaker">{title}</CardTitle>
                        <CardSubtitle>
                            <span className="truncate">{payload.createdBy.fullName}</span>
                        </CardSubtitle>
                    </CardHeader>
                </CardContent>
                {canFold && <CardExpandAction />}
            </CardFront>
            {canFold && (
                <CardExpandContent>
                    <div className="flex min-w-0 grow flex-col gap-0.5">
                        {/* the rich text theme paints paragraphs and list items dark; they take the detail's grey */}
                        {payload.text && (
                            <CardDetail className="[&_li]:text-inherit [&_p]:mb-0 [&_p]:text-inherit">
                                {payload.textRich ? (
                                    <RichTextViewer content={payload.textRich} />
                                ) : (
                                    payload.text
                                )}
                            </CardDetail>
                        )}
                        {value !== undefined && (
                            <CardDetail className="tabular-nums">
                                {t('log__insight_value', { value })}
                            </CardDetail>
                        )}
                    </div>
                </CardExpandContent>
            )}
        </Card>
    );
}
